// Scheduler: overview, jobs, errors and audit over the Cronicle admin API.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Timer,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import {
  useAbortRunMutation,
  useAckErrorsMutation,
  useSchedulerActiveQuery,
  useSchedulerAuditQuery,
  useSchedulerErrorsQuery,
  useSchedulerEventsQuery,
  useSchedulerMetaQuery,
  useSchedulerOverviewQuery,
  schedulerKeys,
} from '@/client/api/scheduler/scheduler.query';
import { ErrorGroup, JobWindow, SchedulerJob, SchedulerWindow } from '@/client/types/scheduler';
import {
  ACTION_LABEL,
  AccessDenied,
  Card,
  CoverageNote,
  EmptyRow,
  ErrorNotice,
  HealthBadge,
  INPUT_CLASS,
  JobLink,
  JobOperateButtons,
  OutcomeBadge,
  PageShell,
  ReasonDialog,
  RunsChart,
  SCHEDULER_BASE,
  StatCard,
  Th,
  JOB_WINDOWS,
  WINDOWS,
  WindowTabs,
  describeTiming,
  fmtAgo,
  fmtDur,
  fmtNum,
  fmtRate,
  fmtTime,
} from './schedulerUi';

type Tab = 'overview' | 'jobs' | 'errors' | 'audit';
const TABS: Tab[] = ['overview', 'jobs', 'errors', 'audit'];

/* ---------- running now ---------- */

const RunningNow: React.FC<{ canOperate: boolean }> = ({ canOperate }) => {
  const activeQ = useSchedulerActiveQuery();
  const abort = useAbortRunMutation();
  const [target, setTarget] = useState<{ id: string; title: string } | null>(null);
  const jobs = activeQ.data?.jobs ?? [];
  const close = () => {
    setTarget(null);
    abort.reset();
  };
  return (
    <Card title="Running now" note={`${jobs.length} active, refreshed every 10 s`} flush>
      {activeQ.isError && (
        <div className="p-4">
          <ErrorNotice error={activeQ.error} onRetry={() => activeQ.refetch()} />
        </div>
      )}
      {!activeQ.isError && (
        <table className="w-full text-sm">
          <tbody>
            {activeQ.isLoading && (
              <EmptyRow cols={5} loading>
                {null}
              </EmptyRow>
            )}
            {!activeQ.isLoading && jobs.length === 0 && (
              <EmptyRow cols={5}>Nothing is running right now</EmptyRow>
            )}
            {jobs.map((j) => (
              <tr key={j.id} className="border-b border-gray-50 last:border-0">
                <td className="px-4 py-2">
                  <JobLink id={j.event} title={j.event_title} />
                  <p className="text-xs text-gray-500">{j.category_title}</p>
                </td>
                <td className="px-4 py-2 text-gray-600">{j.hostname}</td>
                <td className="px-4 py-2">
                  <div className="h-1.5 w-28 overflow-hidden rounded-full bg-gray-100">
                    <div
                      className="h-full rounded-full bg-blue-500"
                      style={{
                        width: `${Math.round(Math.min(1, Math.max(0, j.progress)) * 100)}%`,
                      }}
                    />
                  </div>
                </td>
                <td className="px-4 py-2 text-gray-600">
                  {fmtDur(j.elapsed)}
                  <span className="text-gray-400"> of {fmtDur(j.timeout)}</span>
                </td>
                <td className="px-4 py-2 text-right">
                  <Link
                    to={`${SCHEDULER_BASE}/runs/${encodeURIComponent(j.id)}`}
                    className="mr-3 text-xs text-blue-600 hover:underline"
                  >
                    View
                  </Link>
                  {canOperate && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setTarget({ id: j.id, title: j.event_title })}
                    >
                      Abort
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {target && (
        <ReasonDialog
          open
          onClose={close}
          title={`Abort ${target.title}`}
          description="Stops this run now. The job keeps its schedule."
          confirmLabel="Abort run"
          destructive
          busy={abort.isPending}
          error={abort.error}
          onConfirm={(reason) =>
            abort.mutate({ jobId: target.id, reason: reason || undefined }, { onSuccess: close })
          }
        />
      )}
    </Card>
  );
};

/* ---------- overview ---------- */

const JobMiniTable: React.FC<{
  jobs: SchedulerJob[];
  mode: 'worst' | 'slowest';
  empty: string;
}> = ({ jobs, mode, empty }) => (
  <table className="w-full text-sm">
    <tbody>
      {jobs.length === 0 && <EmptyRow cols={4}>{empty}</EmptyRow>}
      {jobs.map((j) => (
        <tr key={j.id} className="border-b border-gray-50 last:border-0">
          <td className="px-4 py-2">
            <JobLink id={j.id} title={j.title} />
          </td>
          <td className="px-4 py-2">
            <HealthBadge health={j.health} />
          </td>
          {mode === 'worst' ? (
            <>
              <td className="px-4 py-2 text-right text-gray-700">
                {fmtRate(j.stats?.success_rate)}
              </td>
              <td className="px-4 py-2 text-right text-gray-500">
                {j.stats?.consecutive_failures ?? 0} in a row
              </td>
            </>
          ) : (
            <>
              <td className="px-4 py-2 text-right text-gray-700">
                p95 {fmtDur(j.stats?.p95_elapsed)}
              </td>
              <td className="px-4 py-2 text-right text-gray-500">
                max {fmtDur(j.stats?.max_elapsed)}
              </td>
            </>
          )}
        </tr>
      ))}
    </tbody>
  </table>
);

const OverviewTab: React.FC<{ window: JobWindow; canOperate: boolean }> = ({
  window,
  canOperate,
}) => {
  const q = useSchedulerOverviewQuery(window);
  const d = q.data;
  if (q.isError && !d) return <ErrorNotice error={q.error} onRetry={() => q.refetch()} />;
  return (
    <>
      {d?.scheduler_enabled === false && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4" />
          The Cronicle scheduler is paused. No jobs will start.
        </div>
      )}
      <CoverageNote coverage={d?.coverage} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-7">
        <StatCard
          label="Jobs enabled"
          value={d ? `${d.events.enabled} / ${d.events.total}` : '—'}
          hint={d ? `${d.events.failing} failing, ${d.events.degraded} degraded` : undefined}
          icon={<Timer className="h-5 w-5 text-blue-600" />}
          color="bg-blue-50"
        />
        <StatCard
          label={`Runs (${window})`}
          value={fmtNum(d?.runs.total)}
          icon={<Clock className="h-5 w-5 text-indigo-600" />}
          color="bg-indigo-50"
        />
        <StatCard
          label="Success rate"
          value={fmtRate(d?.runs.success_rate)}
          icon={<CheckCircle2 className="h-5 w-5 text-green-600" />}
          color="bg-green-50"
        />
        <StatCard
          label="Failures"
          value={fmtNum(d?.runs.failures)}
          icon={<AlertTriangle className="h-5 w-5 text-red-600" />}
          color="bg-red-50"
        />
        <StatCard
          label="Open error groups"
          value={fmtNum(d?.open_error_groups)}
          icon={<AlertTriangle className="h-5 w-5 text-orange-600" />}
          color="bg-orange-50"
        />
        <StatCard
          label="Running now"
          value={fmtNum(d?.active_jobs)}
          icon={<Loader2 className="h-5 w-5 text-teal-600" />}
          color="bg-teal-50"
        />
        <StatCard
          label="p95 duration"
          value={fmtDur(d?.runs.p95_elapsed)}
          hint={d?.runs.avg_elapsed != null ? `avg ${fmtDur(d.runs.avg_elapsed)}` : undefined}
          icon={<Clock className="h-5 w-5 text-violet-600" />}
          color="bg-violet-50"
        />
      </div>
      <Card
        title="Runs and failures"
        note={
          window === '24h' ? 'per hour' : window === '30d' || window === '90d' ? 'per day' : 'per 6 hours'
        }
      >
        {q.isLoading ? (
          <Skeleton className="h-56 w-full" />
        ) : (
          <RunsChart series={d?.series ?? []} window={window} />
        )}
      </Card>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card title="Needs attention" flush>
          <JobMiniTable jobs={d?.worst ?? []} mode="worst" empty="No job needs attention" />
        </Card>
        <Card title="Slowest" flush>
          <JobMiniTable jobs={d?.slowest ?? []} mode="slowest" empty="No runs in this window" />
        </Card>
      </div>
      <RunningNow canOperate={canOperate} />
    </>
  );
};

/* ---------- jobs ---------- */

const JobsTab: React.FC<{ window: JobWindow; canOperate: boolean; canManage: boolean }> = ({
  window,
  canOperate,
  canManage,
}) => {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [category, setCategory] = useState('');
  const [health, setHealth] = useState('');
  const [enabled, setEnabled] = useState<'' | '0' | '1'>('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  const metaQ = useSchedulerMetaQuery();
  const listQ = useSchedulerEventsQuery({ window, q: debounced, category, health, enabled });
  const rows = listQ.data?.events ?? [];
  const cols = 9;
  return (
    <>
      <CoverageNote coverage={listQ.data?.coverage} />
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search jobs"
            className={`${INPUT_CLASS} w-56 pl-8`}
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className={INPUT_CLASS}
        >
          <option value="">All categories</option>
          {(metaQ.data?.categories ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
            </option>
          ))}
        </select>
        <select value={health} onChange={(e) => setHealth(e.target.value)} className={INPUT_CLASS}>
          <option value="">Any health</option>
          {['healthy', 'degraded', 'failing', 'idle', 'disabled'].map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
        <select
          value={enabled}
          onChange={(e) => setEnabled(e.target.value as '' | '0' | '1')}
          className={INPUT_CLASS}
        >
          <option value="">Enabled or paused</option>
          <option value="1">Enabled</option>
          <option value="0">Paused</option>
        </select>
        <span className="ml-auto text-xs text-gray-500">{listQ.data?.count ?? 0} jobs</span>
        {canManage && (
          <Button size="sm" asChild>
            <Link to={`${SCHEDULER_BASE}/jobs/new`}>
              <Plus className="mr-1 h-4 w-4" />
              New job
            </Link>
          </Button>
        )}
      </div>
      {listQ.isError && <ErrorNotice error={listQ.error} onRetry={() => listQ.refetch()} />}
      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                <Th>Health</Th>
                <Th>Job</Th>
                <Th>Schedule</Th>
                <Th>Last run</Th>
                <Th right>Success</Th>
                <Th right>Avg / p95</Th>
                <Th right>Runs</Th>
                <Th>{null}</Th>
              </tr>
            </thead>
            <tbody>
              {listQ.isLoading && (
                <EmptyRow cols={cols} loading>
                  {null}
                </EmptyRow>
              )}
              {!listQ.isLoading && !listQ.isError && rows.length === 0 && (
                <EmptyRow cols={cols}>No jobs match these filters</EmptyRow>
              )}
              {rows.map((j) => (
                <tr
                  key={j.id}
                  className="border-b border-gray-50 last:border-0 hover:bg-gray-50/70"
                >
                  <td className="px-4 py-2">
                    <HealthBadge health={j.health} />
                  </td>
                  <td className="px-4 py-2">
                    <JobLink id={j.id} title={j.title} />
                    <p className="text-xs text-gray-500">{j.category}</p>
                  </td>
                  <td className="px-4 py-2 text-gray-700">{describeTiming(j.timing)}</td>
                  <td className="px-4 py-2">
                    {j.stats?.last_run ? (
                      <div className="flex items-center gap-2">
                        <Link
                          to={`${SCHEDULER_BASE}/runs/${encodeURIComponent(j.stats.last_run.job_id)}`}
                          className="text-gray-700 hover:underline"
                        >
                          {fmtAgo(j.stats.last_run.at)}
                        </Link>
                        <OutcomeBadge code={j.stats.last_run.code} />
                      </div>
                    ) : (
                      <span className="text-gray-400">no runs</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-700">
                    {fmtRate(j.stats?.success_rate)}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-700">
                    {j.stats
                      ? `${fmtDur(j.stats.avg_elapsed)} / ${fmtDur(j.stats.p95_elapsed)}`
                      : '—'}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-700">{fmtNum(j.stats?.runs)}</td>
                  <td className="px-4 py-2">
                    {canOperate && (
                      <div className="flex justify-end gap-2">
                        <JobOperateButtons job={j} />
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

/* ---------- errors ---------- */

const ErrorGroupCard: React.FC<{ group: ErrorGroup; canOperate: boolean }> = ({
  group,
  canOperate,
}) => {
  const [open, setOpen] = useState(false);
  const [acking, setAcking] = useState(false);
  const ack = useAckErrorsMutation();
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {group.event_enabled === null ? (
              <span className="font-medium text-gray-900">{group.event_title}</span>
            ) : (
              <JobLink id={group.event_id} title={group.event_title} />
            )}
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                group.status === 'open' ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {group.status}
            </span>
            {group.event_enabled === null && (
              <span className="text-xs text-gray-500">job deleted</span>
            )}
            {group.event_enabled === false && <span className="text-xs text-gray-500">paused</span>}
          </div>
          <p className="mt-1 text-sm text-gray-600">
            {group.count} failures, {group.unacknowledged} not acknowledged,{' '}
            {group.consecutive_failures} in a row now. Seen {fmtTime(group.first_at)} to{' '}
            {fmtTime(group.last_at)}.
          </p>
          <div className="mt-1 flex flex-wrap gap-1">
            {Object.entries(group.codes).map(([code, n]) => (
              <span key={code} className="rounded bg-red-50 px-1.5 py-0.5 text-xs text-red-700">
                code {code}: {n}
              </span>
            ))}
          </div>
          {group.ack && (
            <p className="mt-1 text-xs text-gray-500">
              Acknowledged by {group.ack.by} on {fmtTime(group.ack.at)}
              {group.ack.note ? `: ${group.ack.note}` : ''}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setOpen(!open)}>
            {open ? 'Hide samples' : `Samples (${group.samples.length})`}
          </Button>
          {canOperate && group.unacknowledged > 0 && (
            <Button size="sm" onClick={() => setAcking(true)}>
              Acknowledge
            </Button>
          )}
        </div>
      </div>
      {open && (
        <ul className="mt-3 divide-y divide-gray-50 border-t border-gray-100">
          {group.samples.map((s) => (
            <li key={s.job_id} className="flex flex-wrap items-center gap-3 py-2 text-xs">
              <Link
                to={`${SCHEDULER_BASE}/runs/${encodeURIComponent(s.job_id)}`}
                className="text-blue-600 hover:underline"
              >
                {fmtTime(s.at)}
              </Link>
              <span className="rounded bg-red-50 px-1.5 py-0.5 text-red-700">code {s.code}</span>
              <span className="text-gray-500">{fmtDur(s.elapsed)}</span>
              <span className="min-w-0 flex-1 break-all font-mono text-gray-700">
                {s.description}
              </span>
            </li>
          ))}
        </ul>
      )}
      {acking && (
        <ReasonDialog
          open
          onClose={() => {
            setAcking(false);
            ack.reset();
          }}
          title={`Acknowledge failures for ${group.event_title}`}
          description="Marks the current failures as seen. A later failure re-opens the group."
          confirmLabel="Acknowledge"
          busy={ack.isPending}
          error={ack.error}
          onConfirm={(n) =>
            ack.mutate(
              { eventId: group.event_id, note: n || undefined, eventTitle: group.event_title },
              { onSuccess: () => setAcking(false) },
            )
          }
        />
      )}
    </div>
  );
};

const ErrorsTab: React.FC<{ window: SchedulerWindow; canOperate: boolean }> = ({
  window,
  canOperate,
}) => {
  const [status, setStatus] = useState<'open' | 'acknowledged' | 'all'>('open');
  const q = useSchedulerErrorsQuery(window, status);
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-500">
          Failed runs grouped by job. A later failure re-opens an acknowledged group.
        </p>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
          className={INPUT_CLASS}
        >
          <option value="open">Open</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="all">All</option>
        </select>
      </div>
      <CoverageNote coverage={q.data?.coverage} />
      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {q.isLoading && <Skeleton className="h-32 w-full" />}
      {!q.isLoading && !q.isError && (q.data?.groups.length ?? 0) === 0 && (
        <div className="rounded-xl border border-gray-100 bg-white p-8 text-center text-sm text-gray-400 shadow-sm">
          No {status === 'all' ? '' : status} failures in this window.
        </div>
      )}
      <div className="space-y-3">
        {(q.data?.groups ?? []).map((g) => (
          <ErrorGroupCard key={g.event_id} group={g} canOperate={canOperate} />
        ))}
      </div>
    </>
  );
};

/* ---------- audit ---------- */

const AuditTab: React.FC = () => {
  const q = useSchedulerAuditQuery('', 200);
  const rows = q.data?.rows ?? [];
  return (
    <>
      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                <Th>When</Th>
                <Th>Who</Th>
                <Th>Action</Th>
                <Th>Job</Th>
                <Th>Reason</Th>
                <Th>Changed</Th>
              </tr>
            </thead>
            <tbody>
              {q.isLoading && (
                <EmptyRow cols={6} loading>
                  {null}
                </EmptyRow>
              )}
              {!q.isLoading && !q.isError && rows.length === 0 && (
                <EmptyRow cols={6}>No console actions recorded yet</EmptyRow>
              )}
              {rows.map((r, i) => (
                <tr key={`${r.at}-${i}`} className="border-b border-gray-50 last:border-0">
                  <td className="whitespace-nowrap px-4 py-2 text-gray-500">{fmtTime(r.at)}</td>
                  <td className="px-4 py-2 text-gray-700">{r.by}</td>
                  <td className="px-4 py-2 text-gray-900">{ACTION_LABEL[r.action] ?? r.action}</td>
                  <td className="px-4 py-2">
                    {r.action === 'delete' ? (
                      r.event_title
                    ) : (
                      <JobLink id={r.event_id} title={r.event_title || r.event_id} />
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-600">{r.reason || '—'}</td>
                  <td className="px-4 py-2 text-gray-500">
                    {r.fields?.length ? r.fields.join(', ') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

/* ---------- page ---------- */

const SchedulerPage: React.FC<{ initialTab?: Tab }> = ({ initialTab = 'overview' }) => {
  useBreadcrumbs([
    { label: 'Scheduler', href: SCHEDULER_BASE },
    ...(initialTab === 'overview'
      ? []
      : [{ label: initialTab.charAt(0).toUpperCase() + initialTab.slice(1) }]),
  ]);
  const viewPerm = useHasPermission('scheduler.view');
  const operatePerm = useHasPermission('scheduler.operate');
  const managePerm = useHasPermission('scheduler.manage');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canOperate = operatePerm.isSuperAdmin || operatePerm.hasPermission;
  const canManage = managePerm.isSuperAdmin || managePerm.hasPermission;

  const [tab, setTab] = useState<Tab>(initialTab);
  const [window, setWindow] = useState<JobWindow>('24h');
  // Month and quarter figures come from daily totals; individual failures
  // only go back as far as Cronicle keeps them, so Errors stops at 7 days.
  const longWindow = window === '30d' || window === '90d';
  const errorsWindow: SchedulerWindow = longWindow ? '7d' : window;
  useEffect(() => setTab(initialTab), [initialTab]);
  const queryClient = useQueryClient();

  if (viewPerm.isLoading) {
    return (
      <PageShell title="Scheduler" description="">
        <Skeleton className="h-64 w-full" />
      </PageShell>
    );
  }
  if (!canView) {
    return (
      <PageShell title="Scheduler" description="">
        <AccessDenied />
      </PageShell>
    );
  }
  return (
    <PageShell
      title="Scheduler"
      description="Scheduled jobs run by Cronicle: how they are doing, what failed, and what the console changed."
      actions={
        <div className="flex items-center gap-2">
          {tab === 'overview' || tab === 'jobs' ? (
            <WindowTabs value={window} onChange={setWindow} options={JOB_WINDOWS} />
          ) : tab === 'errors' ? (
            <WindowTabs value={errorsWindow} onChange={setWindow} options={WINDOWS} />
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={() => queryClient.invalidateQueries({ queryKey: schedulerKeys.all })}
          >
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            Refresh
          </Button>
        </div>
      }
    >
      <div className="flex gap-2 border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium capitalize ${
              tab === t
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === 'overview' && <OverviewTab window={window} canOperate={canOperate} />}
      {tab === 'jobs' && <JobsTab window={window} canOperate={canOperate} canManage={canManage} />}
      {tab === 'errors' && <ErrorsTab window={errorsWindow} canOperate={canOperate} />}
      {tab === 'audit' && <AuditTab />}
    </PageShell>
  );
};

export default SchedulerPage;
